import React, { useState } from 'react';
import { Key, Search, Filter } from 'lucide-react';
import { usePermissions } from '../../../hooks/useRoles';

const TIPO_COLOR = {
  ver:         'bg-blue-100 text-blue-700',
  crear:       'bg-green-100 text-green-700',
  editar:      'bg-yellow-100 text-yellow-700',
  eliminar:    'bg-red-100 text-red-700',
  aprobar:     'bg-purple-100 text-purple-700',
  procesar:    'bg-orange-100 text-orange-700',
  exportar:    'bg-teal-100 text-teal-700',
  configurar:  'bg-gray-100 text-gray-700',
  importar:    'bg-indigo-100 text-indigo-700',
};

export default function PermissionsModule() {
  const { permissions, byModule, loading, error } = usePermissions();
  const [search, setSearch]       = useState('');
  const [filterModulo, setFilterModulo] = useState('');
  const [filterTipo, setFilterTipo]     = useState('');

  const modulos = Object.keys(byModule).sort();
  const tipos   = [...new Set(permissions.map(p => p.tipo))].sort();

  const filtered = permissions.filter(p => {
    const matchSearch = !search || p.nombre.toLowerCase().includes(search.toLowerCase()) || p.codigo.toLowerCase().includes(search.toLowerCase());
    const matchMod    = !filterModulo || p.modulo === filterModulo;
    const matchTipo   = !filterTipo   || p.tipo === filterTipo;
    return matchSearch && matchMod && matchTipo;
  });

  // Agrupar filtered por módulo
  const grouped = filtered.reduce((acc, p) => {
    if (!acc[p.modulo]) acc[p.modulo] = [];
    acc[p.modulo].push(p);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <Key size={22} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Permisos</h1>
          <p className="text-sm text-gray-500">Catálogo de permisos disponibles en el sistema</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total permisos', value: permissions.length,             color: 'text-indigo-600' },
          { label: 'Módulos',        value: modulos.length,                 color: 'text-blue-600'   },
          { label: 'Tipos',          value: tipos.length,                   color: 'text-purple-600' },
          { label: 'Visibles',       value: filtered.length,                color: 'text-green-600'  },
        ].map(s => (
          <div key={s.label} className="bg-white border rounded-xl p-4 text-center">
            <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-sm text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            placeholder="Buscar permiso o código..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-gray-400" />
          <select
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            value={filterModulo}
            onChange={e => setFilterModulo(e.target.value)}
          >
            <option value="">Todos los módulos</option>
            {modulos.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            value={filterTipo}
            onChange={e => setFilterTipo(e.target.value)}
          >
            <option value="">Todos los tipos</option>
            {tipos.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {error   && <div className="p-4 bg-red-50 text-red-600 rounded-lg mb-4">{error}</div>}
      {loading && <div className="text-center py-12 text-gray-400">Cargando permisos...</div>}

      {/* Tabla agrupada por módulo */}
      {!loading && Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([modulo, perms]) => (
        <div key={modulo} className="mb-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-indigo-400" />
            {modulo}
            <span className="text-xs font-normal text-gray-400">({perms.length})</span>
          </h2>
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-1/3">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Código</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Descripción</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {perms.map(p => (
                  <tr key={p.id} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{p.nombre}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.codigo}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TIPO_COLOR[p.tipo] || 'bg-gray-100 text-gray-600'}`}>
                        {p.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{p.descripcion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed rounded-xl">
          No se encontraron permisos con los filtros aplicados.
        </div>
      )}
    </div>
  );
}
